/**
 * premiumFeatures.js — Premium UX Features Module
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * Features:
 *   7.  Cinematic Welcome Screen — animated 3D landing при запуске без файлов
 *   8.  Picture-in-Picture Comparison — плавающее окно 200×200 для сравнения серий
 *   9.  AI Voice Status — Web Speech API озвучка статусов длительных операций
 *   10. Gesture Shortcuts — распознавание букв/фигур на тачпаде → инструменты
 *   11. Timeline Scrubber — горизонтальная шкала времени серий пациента
 *   12. Dark/Warm Night Mode — автоматический тёплый режим после 20:00
 *   13. Breadcrumb Navigation — цепочка Patient → Study → Series → Slice в topbar
 */

import { on, emit, EVENTS } from '../core/eventBus.js';
import { log } from '../utils/logger.js';
import { решениеОЗаставке, ПОВОДЫ } from './welcomeGate.js';

// [FIX] AbortController для очистки всех addEventListener при destroy
let _ac = new AbortController();

/**
 * [10.3.2] Номер версии на заставке — под словом Cornerstone4D.
 *
 * Здесь стояло «DICOM Viewer Pro v5» — цифра из времён, когда сборка и правда
 * была пятой, вбитая в текст руками. С тех пор вышло больше сотни версий, а на
 * заставке по-прежнему было v5. __APP_VERSION__ подставляет сборщик из
 * package.json, так что разойтись с настоящей версией она больше не может.
 */
const APP_VER = (typeof __APP_VERSION__ !== 'undefined') ? __APP_VERSION__ : '';


// ════════════════════════════════════════════════════════════════
// 7. CINEMATIC WELCOME SCREEN
// ════════════════════════════════════════════════════════════════

const WELCOME_ID = 'cinematic-welcome';

function createWelcomeScreen() {
  if (document.getElementById(WELCOME_ID)) return;

  const wrapper = document.getElementById('viewport-wrapper');
  if (!wrapper) return;

  const el = document.createElement('div');
  el.id = WELCOME_ID;
  el.innerHTML = `
    <canvas id="welcome-canvas"></canvas>
    <div class="welcome-content">
      <!-- Animated CT scan line -->
      <div class="welcome-scan-viz">
        <svg viewBox="0 0 280 280" class="scan-svg">
          <defs>
            <radialGradient id="scanGrad" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stop-color="rgba(79,140,255,0.12)"/>
              <stop offset="70%" stop-color="rgba(79,140,255,0.03)"/>
              <stop offset="100%" stop-color="transparent"/>
            </radialGradient>
            <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="transparent"/>
              <stop offset="30%" stop-color="rgba(34,211,238,0.6)"/>
              <stop offset="50%" stop-color="rgba(79,140,255,0.9)"/>
              <stop offset="70%" stop-color="rgba(34,211,238,0.6)"/>
              <stop offset="100%" stop-color="transparent"/>
            </linearGradient>
          </defs>
          <!-- Background circles (CT scanner rings) -->
          <circle cx="140" cy="140" r="130" fill="none" stroke="rgba(79,140,255,0.06)" stroke-width="1"/>
          <circle cx="140" cy="140" r="100" fill="none" stroke="rgba(79,140,255,0.04)" stroke-width="0.5" stroke-dasharray="4 6"/>
          <circle cx="140" cy="140" r="70" fill="none" stroke="rgba(79,140,255,0.06)" stroke-width="0.5"/>
          <circle cx="140" cy="140" r="130" fill="url(#scanGrad)"/>
          <!-- Rotating scanner arc -->
          <g class="scan-rotate">
            <path d="M140,10 A130,130 0 0,1 264,100" fill="none" stroke="rgba(79,140,255,0.3)" stroke-width="2" stroke-linecap="round"/>
            <circle cx="264" cy="100" r="3" fill="#4f8cff" class="scan-dot"/>
          </g>
          <!-- Scan line (sweeping) -->
          <line x1="10" y1="140" x2="270" y2="140" stroke="url(#lineGrad)" stroke-width="1.5" class="scan-line"/>
          <!-- Grid overlay -->
          <line x1="140" y1="20" x2="140" y2="260" stroke="rgba(79,140,255,0.04)" stroke-width="0.5"/>
          <line x1="20" y1="140" x2="260" y2="140" stroke="rgba(79,140,255,0.04)" stroke-width="0.5"/>
          <!-- Corner markers -->
          <path d="M30,30 L50,30 M30,30 L30,50" fill="none" stroke="rgba(79,140,255,0.15)" stroke-width="1"/>
          <path d="M250,30 L230,30 M250,30 L250,50" fill="none" stroke="rgba(79,140,255,0.15)" stroke-width="1"/>
          <path d="M30,250 L50,250 M30,250 L30,230" fill="none" stroke="rgba(79,140,255,0.15)" stroke-width="1"/>
          <path d="M250,250 L230,250 M250,250 L250,230" fill="none" stroke="rgba(79,140,255,0.15)" stroke-width="1"/>
          <!-- Center crosshair -->
          <line x1="133" y1="140" x2="147" y2="140" stroke="rgba(34,211,238,0.5)" stroke-width="1"/>
          <line x1="140" y1="133" x2="140" y2="147" stroke="rgba(34,211,238,0.5)" stroke-width="1"/>
        </svg>
      </div>

      <div class="welcome-hero-text">
        <div class="welcome-label">MEDICAL IMAGING WORKSTATION</div>
        <h1 class="welcome-title">Cornerstone<span class="welcome-4d">4D</span></h1>
        <div class="welcome-version">DICOM Viewer Pro <b class="welcome-ver-num">v${APP_VER}</b> — by Dr. Mikhmel</div>
      </div>

      <div class="welcome-drop-zone" id="welcome-drop-target">
        <div class="welcome-drop-ring"></div>
        <div class="welcome-drop-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(79,140,255,0.7)" stroke-width="1.5" stroke-linecap="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
        </div>
        <div class="welcome-drop-text">Перетащите DICOM сюда</div>
        <div class="welcome-drop-hint">или нажмите для выбора папки</div>
      </div>

      <!-- [10.19.37] Быстрый вход с заставки: владелец замерил на телефоне
           202 с папкой против ~8 с архивом на тех же срезах. Кнопка видна
           всем; приписка про телефон добавляется ниже только на касании. -->
      <button id="welcome-open-zip" style="margin-top:14px;padding:10px 20px;border-radius:10px;border:1px solid rgba(79,140,255,.45);background:rgba(79,140,255,.12);color:#cfe0ff;font-size:13px;cursor:pointer;">📦 Открыть ZIP-архив</button>
      <div id="welcome-zip-note" style="margin-top:6px;font-size:11px;color:rgba(160,190,235,.78);display:none;">на телефоне архив открывается в десятки раз быстрее папки</div>

      <div class="welcome-stats">
        <div class="ws-item"><span class="ws-val">3D</span><span class="ws-label">Volume Rendering</span></div>
        <div class="ws-sep"></div>
        <div class="ws-item"><span class="ws-val">AI</span><span class="ws-label">On-Device MedSAM</span></div>
        <div class="ws-sep"></div>
        <div class="ws-item"><span class="ws-val">840+</span><span class="ws-label">Функций</span></div>
        <div class="ws-sep"></div>
        <div class="ws-item"><span class="ws-val">WebGPU</span><span class="ws-label">Ускорение</span></div>
        <div class="ws-sep"></div>
        <!-- [10.3.2] «PWA / Offline» с заставки убрано: слово из мира
             разработчиков, врачу оно ничего не говорит. -->
      </div>

      <div class="welcome-tech">
        Cornerstone3D &bull; VTK.js &bull; ONNX Runtime &bull; ITK-WASM &bull; Cinematic Rendering &bull; DICOMweb
      </div>
    </div>
  `;
  wrapper.appendChild(el);

  // Particle canvas
  initParticleCanvas();

  // Drop zone forwarding
  // [10.19.37] Кнопка архива живёт РЯДОМ с зоной папки, а не внутри неё, и
  // всё же глушит всплытие: заставка целиком кликабельна в некоторых слоях.
  el.querySelector('#welcome-open-zip')?.addEventListener('click', (e) => {
    e.stopPropagation();
    document.getElementById('btn-import-zip')?.click();
  }, { signal: _ac.signal });
  try {
    const касание = navigator.maxTouchPoints > 0 || window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
    if (касание) { const n = el.querySelector('#welcome-zip-note'); if (n) n.style.display = ''; }
  } catch (e) { /* приписка — не повод ронять заставку */ }

  const dropTarget = el.querySelector('#welcome-drop-target');
  ['dragenter','dragover'].forEach(evt => {
    dropTarget.addEventListener(evt, e => { e.preventDefault(); e.stopPropagation(); el.classList.add('drag-active'); }, { signal: _ac.signal });
  });
  ['dragleave','drop'].forEach(evt => {
    dropTarget.addEventListener(evt, e => { el.classList.remove('drag-active'); }, { signal: _ac.signal });
  });
  dropTarget.addEventListener('drop', e => {
    e.preventDefault(); e.stopPropagation();
    // Forward to main file handler via hidden input or global handler
    const dt = e.dataTransfer;
    if (dt?.files?.length) {
      const fileInput = document.getElementById('file-input');
      if (fileInput) {
        // Create new DataTransfer and assign
        const transfer = new DataTransfer();
        for (const f of dt.files) transfer.items.add(f);
        fileInput.files = transfer.files;
        fileInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }, { signal: _ac.signal });
  dropTarget.addEventListener('click', () => {
    // [10.19.34] Здесь была та самая тишина владельца — 18 секунд на телефоне.
    // Стоял прямой folderInput.click() с самообманом в комментарии: «тот же
    // input, что и „Файл → Открыть папку“». Input тот же — ПУТЬ другой: окно
    // ожидания с тикающими секундами («Читаю список файлов… Прошло N с») и
    // замер обхода живут в openFolderPicker, а прямой клик их обходил. Врач
    // тапал приветственный экран — и смотрел в 18 секунд полной тишины, пока
    // браузер молча собирал список. Летопись 10.1.9 хоронила ЭТУ ЖЕ болезнь
    // на кнопке меню; экран приветствия остался заражённым, а правильный
    // путь стоял… запасным в else. Теперь путь один — канонический.
    document.getElementById('btn-open-folder')?.click();
  }, { signal: _ac.signal });

  log.debug('[Premium] Cinematic Welcome Screen mounted');
}

function initParticleCanvas() {
  const canvas = document.getElementById('welcome-canvas');
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  let particles = [];
  let animId = null;
  let w, h;

  function resize() {
    const rect = canvas.parentElement.getBoundingClientRect();
    w = canvas.width = rect.width;
    h = canvas.height = rect.height;
  }

  function createParticles() {
    particles = [];
    const count = Math.min(80, Math.floor((w * h) / 8000));
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.5 + 0.5,
        alpha: Math.random() * 0.4 + 0.1,
        hue: Math.random() > 0.5 ? 215 : 190 // blue / cyan
      });
    }
  }

  function draw() {
    ctx.clearRect(0, 0, w, h);
    // Draw connections
    for (let i = 0; i < particles.length; i++) {
      for (let j = i + 1; j < particles.length; j++) {
        const dx = particles[i].x - particles[j].x;
        const dy = particles[i].y - particles[j].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 120) {
          const alpha = (1 - dist / 120) * 0.08;
          ctx.strokeStyle = `rgba(79,140,255,${alpha})`;
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(particles[i].x, particles[i].y);
          ctx.lineTo(particles[j].x, particles[j].y);
          ctx.stroke();
        }
      }
    }
    // Draw particles
    for (const p of particles) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = `hsla(${p.hue},80%,70%,${p.alpha})`;
      ctx.fill();
      // Move
      p.x += p.vx; p.y += p.vy;
      if (p.x < 0 || p.x > w) p.vx *= -1;
      if (p.y < 0 || p.y > h) p.vy *= -1;
    }
    animId = requestAnimationFrame(draw);
  }

  resize();
  createParticles();
  draw();

  const ro = new ResizeObserver(() => { resize(); createParticles(); });
  ro.observe(canvas.parentElement);

  // Store cleanup ref
  canvas._particleCleanup = () => { cancelAnimationFrame(animId); ro.disconnect(); };
}

/**
 * [11.0.6] Вернуть заставку — после «Очистить исследования».
 *
 * Снятие заставки было, а показа обратно — нет: она создавалась один раз при
 * запуске. После очистки врач оставался перед пустыми чёрными окнами без
 * единой подсказки, что делать дальше. Теперь программа снова предлагает
 * открыть папку — как при первом запуске.
 *
 * Правило welcomeGate этого не делает и не должно: оно отвечает на вопрос
 * «когда СНИМАТЬ заставку», а не «когда показать её снова».
 */
export function вернутьЗаставку() {
  const прежняя = document.getElementById(WELCOME_ID);
  if (прежняя) снятьЗаставкуСразу(прежняя);
  createWelcomeScreen();
  const свежая = document.getElementById(WELCOME_ID);
  if (свежая) свежая.classList.remove('dismissing');
}

/** Убрать старый узел заставки немедленно, без плавного ухода. */
function снятьЗаставкуСразу(el) {
  const canvas = document.getElementById('welcome-canvas');
  if (canvas?._particleCleanup) canvas._particleCleanup();
  el.remove();
}

/**
 * [11.1.7] Снять заставку — теперь ЭКСПОРТИРУЕТСЯ.
 *
 * Повод: владелец нажимал «Сравнение», в журнале честно писалось «режим
 * включён, раскладка 1x2» — а на экране двух окон не было. Окна создавались,
 * но их ЗАКРЫВАЛА СТАРТОВАЯ ЗАСТАВКА: она лежит внутри контейнера окон с
 * `position: absolute; inset: 0; z-index: 10`, то есть поверх всего, что там
 * нарисовано.
 *
 * Снималась заставка только по факту появления снимков. Вошёл в сравнение с
 * пустой программой — окна под ней, и выглядит это как «кнопка не работает».
 *
 * Моя доля вины здесь прямая: в 11.0.8 я научил «Очистить исследования»
 * ВОЗВРАЩАТЬ заставку. До этого она пропадала навсегда после первой загрузки,
 * и наткнуться на такое можно было только сразу после запуска.
 */
export function снятьЗаставку() { dismissWelcome(); }

function dismissWelcome() {
  const el = document.getElementById(WELCOME_ID);
  if (!el) return;
  el.classList.add('dismissing');
  const canvas = document.getElementById('welcome-canvas');
  if (canvas?._particleCleanup) canvas._particleCleanup();
  setTimeout(() => el.remove(), 600);
}

/**
 * Снятие заставки — по ФАКТУ СНИМКОВ. [10.19.22]
 *
 * Здесь стоял наблюдатель за `#viewport-container`: любой добавленный узел
 * снимал заставку. Стартовая раскладка создаёт пустое окно ещё до всякой
 * загрузки — и заставку снимало на первой миллисекунде, оставляя врача перед
 * чёрным прямоугольником с подписью «Нативные срезы». Разбор и правило —
 * в welcomeGate.js; здесь только подстановка чисел из событий.
 *
 * Наблюдателя за контейнером окон больше нет вовсе: этот признак ложен по
 * построению, и «починить» его условием нельзя. Взамен добавлен наблюдатель за
 * СПИСКОМ СЕРИЙ — он говорит именно то, что нужно: исследование открыто.
 */
function hookWelcomeDismiss() {
  const судить = (повод, состояние) => {
    const р = решениеОЗаставке({ повод, ...состояние });
    if (р.снимать) {
      log.debug(`[Premium] заставка снята: ${р.почему}`);
      dismissWelcome();
    }
    return р.снимать;
  };

  on(EVENTS.STACK_LOADED, (d) => судить(ПОВОДЫ.СТОПКА, { кадров: d?.count ?? d?.imageIds?.length ?? 0 }));
  on(EVENTS.VOLUME_LOAD_START, (d) => судить(ПОВОДЫ.ТОМ, { кадров: d?.imageCount ?? 0 }));
  on('series:selected', () => судить(ПОВОДЫ.СЕРИЯ, {}));
  on('app:filesLoaded', (d) => судить(ПОВОДЫ.СТОПКА, { кадров: d?.count ?? d?.files?.length ?? 1 }));

  // Список серий: единственный наблюдатель, который здесь уместен. Записи в нём
  // появляются только когда исследование разобрано, а не когда создано окно.
  //
  // Считаются ИМЕННО записи серий. Запасного `children.length` здесь быть не
  // может: в пустом списке лежит заглушка «Загрузите DICOM для отображения
  // серий», и её перерисовка дала бы единицу — то есть ту же ложную причину
  // снятия, из-за которой всё и затевалось, только с другой стороны.
  const список = document.getElementById('series-list');
  if (список) {
    const mo = new MutationObserver(() => {
      const серий = список.querySelectorAll('.series-item, [data-series-uid]').length;
      if (судить(ПОВОДЫ.СПИСОК, { серий })) mo.disconnect();
    });
    mo.observe(список, { childList: true });
  }
}


// ════════════════════════════════════════════════════════════════
// 8. PICTURE-IN-PICTURE COMPARISON
// ════════════════════════════════════════════════════════════════

let _pipWindow = null;

/**
 * Создаёт PiP-окно со снимком серии поверх viewport.
 * @param {HTMLCanvasElement|string} sourceCanvas — canvas элемент или data URL изображения
 * @param {string} [label] — подпись (серия, дата)
 */
export function showPiP(sourceCanvas, label = '') {
  closePiP(); // только один PiP одновременно

  const wrapper = document.getElementById('viewport-wrapper');
  if (!wrapper) return;

  const pip = document.createElement('div');
  pip.id = 'pip-window';
  pip.className = 'pip-window';
  pip.innerHTML = `
    <div class="pip-header">
      <span class="pip-label">${label || 'Сравнение'}</span>
      <div class="pip-controls">
        <button class="pip-btn pip-resize" title="Размер">⤡</button>
        <button class="pip-btn pip-close" title="Закрыть">✕</button>
      </div>
    </div>
    <div class="pip-body">
      <canvas class="pip-canvas" width="200" height="200"></canvas>
    </div>
    <div class="pip-footer">
      <input type="range" class="pip-opacity" min="30" max="100" value="100" title="Прозрачность">
    </div>
  `;
  wrapper.appendChild(pip);

  // Copy image to PiP canvas
  const pipCanvas = pip.querySelector('.pip-canvas');
  const pipCtx = pipCanvas.getContext('2d');

  if (typeof sourceCanvas === 'string') {
    const img = new Image();
    img.onload = () => {
      pipCanvas.width = 200; pipCanvas.height = 200;
      pipCtx.drawImage(img, 0, 0, 200, 200);
    };
    img.src = sourceCanvas;
  } else if (sourceCanvas instanceof HTMLCanvasElement) {
    pipCanvas.width = 200; pipCanvas.height = 200;
    pipCtx.drawImage(sourceCanvas, 0, 0, 200, 200);
  }

  // Draggable
  let isDragging = false, startX, startY, origX, origY;
  const header = pip.querySelector('.pip-header');
  header.addEventListener('pointerdown', (e) => {
    isDragging = true;
    startX = e.clientX; startY = e.clientY;
    const rect = pip.getBoundingClientRect();
    origX = rect.left; origY = rect.top;
    header.setPointerCapture(e.pointerId);
    pip.style.transition = 'none';
  }, { signal: _ac.signal });
  header.addEventListener('pointermove', (e) => {
    if (!isDragging) return;
    const dx = e.clientX - startX, dy = e.clientY - startY;
    pip.style.left = (origX + dx) + 'px';
    pip.style.top = (origY + dy) + 'px';
    pip.style.right = 'auto'; pip.style.bottom = 'auto';
  }, { signal: _ac.signal });
  header.addEventListener('pointerup', () => { isDragging = false; pip.style.transition = ''; }, { signal: _ac.signal });

  // Close
  pip.querySelector('.pip-close').addEventListener('click', closePiP, { signal: _ac.signal });

  // Resize toggle (200 ↔ 300)
  let enlarged = false;
  pip.querySelector('.pip-resize').addEventListener('click', () => {
    enlarged = !enlarged;
    const size = enlarged ? 300 : 200;
    pip.style.width = size + 'px';
    pipCanvas.width = size; pipCanvas.height = size;
    if (typeof sourceCanvas === 'string') {
      const img = new Image();
      img.onload = () => pipCtx.drawImage(img, 0, 0, size, size);
      img.src = sourceCanvas;
    } else if (sourceCanvas instanceof HTMLCanvasElement) {
      pipCtx.drawImage(sourceCanvas, 0, 0, size, size);
    }
  }, { signal: _ac.signal });

  // Opacity
  pip.querySelector('.pip-opacity').addEventListener('input', (e) => {
    pip.querySelector('.pip-body').style.opacity = e.target.value / 100;
  }, { signal: _ac.signal });

  _pipWindow = pip;
  log.debug('[Premium] PiP window opened');
}

export function closePiP() {
  if (_pipWindow) { _pipWindow.remove(); _pipWindow = null; }
}

/**
 * Capture current viewport canvas as PiP snapshot.
 * Вызывается из контекстного меню или drag серии на угол.
 */
export function captureViewportToPiP(viewportElement, label) {
  if (!viewportElement) return;
  const canvas = viewportElement.querySelector('canvas');
  if (!canvas) return;
  showPiP(canvas, label);
}


// ════════════════════════════════════════════════════════════════
// 9. AI VOICE STATUS
// ════════════════════════════════════════════════════════════════

const _voiceStatus = {
  enabled: false,
  lang: 'ru-RU',
  queue: [],
  speaking: false
};

// [11.1.36] Настройка озвучки ЖИВЁТ между сеансами и по умолчанию ВЫКЛЮЧЕНА
// (просьба владельца: «по умолчанию полностью отключить озвучку количества
// загруженных кадров»). Кто включил кнопкой «Голос: озвучка» — у того
// останется включённой.
export const КЛЮЧ_ОЗВУЧКИ = 'mikhmel_voice_status';
function озвучкаСохраненаВкл() {
  try { return localStorage.getItem(КЛЮЧ_ОЗВУЧКИ) === 'on'; } catch (e) { return false; }
}

export function enableVoiceStatus(lang = 'ru-RU') {
  if (!('speechSynthesis' in window)) {
    log.warn('[VoiceStatus] Web Speech API not supported');
    return false;
  }
  _voiceStatus.enabled = true;
  _voiceStatus.lang = lang;
  try { localStorage.setItem(КЛЮЧ_ОЗВУЧКИ, 'on'); } catch (e) { /* приватный режим */ }
  log.debug(`[Premium] AI Voice Status enabled (${lang})`);
  return true;
}

export function disableVoiceStatus() {
  _voiceStatus.enabled = false;
  try { localStorage.setItem(КЛЮЧ_ОЗВУЧКИ, 'off'); } catch (e) { /* приватный режим */ }
  window.speechSynthesis?.cancel();
}

export function toggleVoiceStatus() {
  if (_voiceStatus.enabled) { disableVoiceStatus(); return false; }
  else { return enableVoiceStatus(_voiceStatus.lang); }
}

function speak(text) {
  // [11.1.37] ГЛАВНЫЙ ЗАСЛОН — ЗДЕСЬ, ВО РТУ. Всё это время speak() не
  // проверял _voiceStatus.enabled ВООБЩЕ: включатель, выключатель и кнопка
  // «Голос: озвучка» были декорациями на трубе без крана — событие пришло,
  // голос сказал. Потому и правка 11.1.36 (по умолчанию выкл) на слух ничего
  // не изменила: она закрыла кран, которого не существовало. Владелец поймал
  // это журналом: «[Voice] speak called: Загружено 818 срезов» на свежей
  // сборке. Кран поставлен; console.log переведён в log.debug — он тоже
  // спамил на каждую загрузку.
  if (!_voiceStatus.enabled) return;
  log.debug('[Voice] speak:', text);
  if (!('speechSynthesis' in window)) return;
  
  const synth = window.speechSynthesis;
  if (_voiceStatus.speaking) {
    _voiceStatus.queue.push(text);
    return;
  }
  
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'ru-RU';
  utterance.rate = 1.1;
  utterance.pitch = 1.0;
  utterance.volume = 0.9;

  const voices = synth.getVoices();
  const preferred = voices.find(v => v.lang.startsWith('ru') && v.localService);
  if (preferred) utterance.voice = preferred;

  utterance.onend = () => {
    _voiceStatus.speaking = false;
    if (_voiceStatus.queue.length > 0) {
      speak(_voiceStatus.queue.shift());
    }
  };
  utterance.onerror = (e) => { console.warn('[Voice] error:', e.error); _voiceStatus.speaking = false; };

  _voiceStatus.speaking = true;
  synth.speak(utterance);
}

/** Hook into app events for voice announcements */
function hookVoiceEvents() {
  // [FIX] Chrome requires user gesture before speechSynthesis works.
  // Enable voice on the first click anywhere on the page.
  // [11.1.36] НАДГРОБИЕ ПРИНУДИТЕЛЬНОГО ВКЛЮЧЕНИЯ. Здесь стояло безусловное
  // enableVoiceStatus('ru-RU') по ПЕРВОМУ клику где угодно: «жест для Chrome»
  // превратили во включатель. Итог — озвучка «Загрузка завершена. 818 срезов»
  // орала у всех всегда, а кнопка «Голос: озвучка» была бессильна: следующий
  // сеанс включал её заново. Теперь жест лишь БУДИТ speechSynthesis, и только
  // если врач сам сохранил озвучку включённой.
  let _voiceAutoEnabled = false;
  function autoEnableVoice() {
    if (_voiceAutoEnabled) return;
    _voiceAutoEnabled = true;
    if (!озвучкаСохраненаВкл()) return;   // по умолчанию — тишина
    enableVoiceStatus('ru-RU');
    // Warm up speechSynthesis with a silent utterance (Chrome needs this)
    try {
      const warmup = new SpeechSynthesisUtterance('');
      warmup.volume = 0;
      window.speechSynthesis?.speak(warmup);
    } catch (e) { /* safe */ }
  }
  document.addEventListener('click', autoEnableVoice, { once: true });
  document.addEventListener('keydown', autoEnableVoice, { once: true });

  on(EVENTS.VOLUME_LOAD_DONE, (detail) => {
    console.log('[Voice] VOLUME_LOAD_DONE fired:', detail);
    const slices = detail?.sliceCount || detail?.numSlices || detail?.framesLoaded || '';
    speak(slices ? `Загрузка завершена. ${slices} срезов.` : 'Загрузка завершена.');
  });

  on(EVENTS.STACK_LOADED, (detail) => {
    console.log('[Voice] STACK_LOADED fired:', detail);
    const count = detail?.imageIds?.length || detail?.count || '';
    if (count) speak(`Загружено ${count} срезов.`);
  });

  on('ai:segmentation:done', (detail) => {
    const organ = detail?.organ || 'область';
    const findings = detail?.findings || '';
    speak(`Сегментация ${organ} завершена.${findings ? ` Найдено: ${findings}.` : ''}`);
  });

  on('ai:analysis:done', (detail) => {
    speak(detail?.summary || 'Анализ завершен.');
  });

  on(EVENTS.CALCIUM_SCORE_DONE, (detail) => {
    const score = detail?.score ?? detail?.totalScore ?? '';
    speak(`Кальциевый индекс: ${score}.`);
  });

  // Generic long-operation hook
  on('operation:complete', (detail) => {
    if (detail?.message) speak(detail.message);
  });
}


// ════════════════════════════════════════════════════════════════
// 10. GESTURE SHORTCUTS (shape recognition)
// ════════════════════════════════════════════════════════════════

const _gestureState = {
  active: false,
  points: [],
  canvas: null,
  ctx: null,
  timeout: null
};

export function enableGestureShortcuts() {
  if (_gestureState.active) return;
  _gestureState.active = true;

  // Create overlay canvas for gesture drawing
  const wrapper = document.getElementById('viewport-wrapper');
  if (!wrapper) return;

  let gestureCanvas = document.getElementById('gesture-shortcut-canvas');
  if (!gestureCanvas) {
    gestureCanvas = document.createElement('canvas');
    gestureCanvas.id = 'gesture-shortcut-canvas';
    gestureCanvas.className = 'gesture-shortcut-canvas';
    wrapper.appendChild(gestureCanvas);
  }

  _gestureState.canvas = gestureCanvas;
  _gestureState.ctx = gestureCanvas.getContext('2d');

  // Resize
  function resize() {
    const rect = wrapper.getBoundingClientRect();
    gestureCanvas.width = rect.width;
    gestureCanvas.height = rect.height;
  }
  resize();
  const ro = new ResizeObserver(resize);
  ro.observe(wrapper);

  // 3-finger touch or Ctrl+right-mouse triggers gesture mode
  let gestureMode = false;
  let drawingPoints = [];

  wrapper.addEventListener('pointerdown', (e) => {
    // Ctrl + left click = gesture mode
    if (e.ctrlKey && e.altKey && e.button === 0) {
      gestureMode = true;
      drawingPoints = [{ x: e.offsetX, y: e.offsetY }];
      gestureCanvas.style.pointerEvents = 'auto';
      gestureCanvas.style.opacity = '1';
      e.preventDefault();
    }
  }, { capture: true, signal: _ac.signal });

  gestureCanvas.addEventListener('pointermove', (e) => {
    if (!gestureMode) return;
    drawingPoints.push({ x: e.offsetX, y: e.offsetY });
    drawGesturePath(drawingPoints);
  }, { signal: _ac.signal });

  gestureCanvas.addEventListener('pointerup', () => {
    if (!gestureMode) return;
    gestureMode = false;
    gestureCanvas.style.pointerEvents = 'none';

    if (drawingPoints.length > 10) {
      const recognized = recognizeShape(drawingPoints);
      if (recognized) {
        showGestureResult(recognized);
        activateToolByGesture(recognized);
      }
    }

    // Fade out
    setTimeout(() => {
      _gestureState.ctx.clearRect(0, 0, gestureCanvas.width, gestureCanvas.height);
      gestureCanvas.style.opacity = '0';
    }, 500);
    drawingPoints = [];
  }, { signal: _ac.signal });

  log.debug('[Premium] Gesture Shortcuts enabled (Ctrl+Alt+Draw)');
}

function drawGesturePath(points) {
  const ctx = _gestureState.ctx;
  const canvas = _gestureState.canvas;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  if (points.length < 2) return;

  ctx.strokeStyle = 'rgba(79,140,255,0.8)';
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.shadowColor = '#4f8cff';
  ctx.shadowBlur = 8;
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.stroke();
  ctx.shadowBlur = 0;
}

/**
 * Simple shape recognition: L, Z, V, circle, line
 * Uses angular direction changes and bounding box ratio
 */
function recognizeShape(points) {
  if (points.length < 5) return null;

  // Simplify with Ramer-Douglas-Peucker (simplified)
  const simplified = simplifyPoints(points, 8);
  if (simplified.length < 3) return null;

  // Bounding box
  const xs = simplified.map(p => p.x), ys = simplified.map(p => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const bw = maxX - minX, bh = maxY - minY;
  const ratio = bw / (bh || 1);

  // Calculate cumulative angle
  let totalAngle = 0;
  const directions = [];
  for (let i = 1; i < simplified.length; i++) {
    const dx = simplified[i].x - simplified[i-1].x;
    const dy = simplified[i].y - simplified[i-1].y;
    directions.push(Math.atan2(dy, dx));
  }
  for (let i = 1; i < directions.length; i++) {
    let da = directions[i] - directions[i-1];
    if (da > Math.PI) da -= 2 * Math.PI;
    if (da < -Math.PI) da += 2 * Math.PI;
    totalAngle += da;
  }

  // Circle: totalAngle close to ±2π, aspect ratio ~1
  if (Math.abs(Math.abs(totalAngle) - 2 * Math.PI) < 1.2 && ratio > 0.5 && ratio < 2) {
    return 'circle';
  }

  // Count significant direction changes (corners)
  const corners = findCorners(simplified);

  // "L" shape: 1 corner, two roughly perpendicular strokes
  if (corners.length === 1) {
    const angle = corners[0].angle;
    if (Math.abs(Math.abs(angle) - Math.PI / 2) < 0.6) return 'L';
  }

  // "Z" shape: 2 corners
  if (corners.length === 2) {
    return 'Z';
  }

  // "V" shape: 1 corner, narrow angle
  if (corners.length === 1) {
    const angle = corners[0].angle;
    if (Math.abs(angle) < 1.2 && Math.abs(angle) > 0.3) return 'V';
  }

  // Straight horizontal line
  if (corners.length === 0 && bh < 30 && bw > 80) return 'line-h';

  return null;
}

function findCorners(points) {
  const corners = [];
  const threshold = 0.5; // radians

  for (let i = 1; i < points.length - 1; i++) {
    const dx1 = points[i].x - points[i-1].x, dy1 = points[i].y - points[i-1].y;
    const dx2 = points[i+1].x - points[i].x, dy2 = points[i+1].y - points[i].y;
    const a1 = Math.atan2(dy1, dx1), a2 = Math.atan2(dy2, dx2);
    let da = a2 - a1;
    if (da > Math.PI) da -= 2 * Math.PI;
    if (da < -Math.PI) da += 2 * Math.PI;
    if (Math.abs(da) > threshold) {
      corners.push({ index: i, angle: da, point: points[i] });
    }
  }
  return corners;
}

function simplifyPoints(pts, tolerance) {
  if (pts.length <= 2) return pts;
  // Uniform sampling for speed
  const step = Math.max(1, Math.floor(pts.length / 20));
  const result = [pts[0]];
  for (let i = step; i < pts.length - 1; i += step) result.push(pts[i]);
  result.push(pts[pts.length - 1]);
  return result;
}

function activateToolByGesture(shape) {
  const toolMap = {
    'L': 'Length',           // Линейка
    'Z': 'Zoom',            // Zoom
    'circle': 'EllipticalROI', // Эллипс ROI
    'V': 'Angle',           // Угол
    'line-h': 'WindowLevel'  // Window/Level
  };
  const tool = toolMap[shape];
  if (tool) {
    emit(EVENTS.TOOL_CHANGED, { toolName: tool, source: 'gesture' });
    // Try to activate through toolManager if available
    try {
      const btn = document.querySelector(`[data-tool="${tool}"]`);
      if (btn) btn.click();
    } catch (e) { log.debug("[premiumFeatures]", e.message); }
  }
}

function showGestureResult(shape) {
  const nameMap = {
    'L': '📏 Линейка (Length)',
    'Z': '🔍 Zoom',
    'circle': '⭕ EllipticalROI',
    'V': '📐 Угол (Angle)',
    'line-h': '🌓 Window/Level'
  };
  const name = nameMap[shape] || shape;
  
  // Show floating indicator
  const indicator = document.createElement('div');
  indicator.className = 'gesture-result-indicator';
  indicator.textContent = name;
  document.getElementById('viewport-wrapper')?.appendChild(indicator);
  setTimeout(() => indicator.classList.add('visible'), 10);
  setTimeout(() => { indicator.classList.remove('visible'); setTimeout(() => indicator.remove(), 300); }, 1500);
}


// ════════════════════════════════════════════════════════════════
// 11. TIMELINE SCRUBBER
// ════════════════════════════════════════════════════════════════

const _timelineState = {
  studies: [], // { date, label, seriesId, modality }
  element: null
};

/**
 * Обновить Timeline данными пациента
 * @param {Array} studies — массив { date: Date|string, label: string, seriesUID: string, modality: string }
 */
export function updateTimeline(studies) {
  _timelineState.studies = studies.sort((a, b) => new Date(a.date) - new Date(b.date));
  renderTimeline();
}

function createTimelineElement() {
  if (document.getElementById('timeline-scrubber')) return;

  const wrapper = document.getElementById('viewport-wrapper');
  if (!wrapper) return;

  const el = document.createElement('div');
  el.id = 'timeline-scrubber';
  el.className = 'timeline-scrubber';
  el.innerHTML = `
    <div class="timeline-track">
      <div class="timeline-line"></div>
      <div class="timeline-points" id="timeline-points"></div>
    </div>
    <button class="timeline-toggle" id="timeline-toggle" title="Временная шкала исследований">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="2" y1="12" x2="22" y2="12"/><circle cx="6" cy="12" r="2"/><circle cx="14" cy="12" r="2"/><circle cx="20" cy="12" r="2"/></svg>
    </button>
  `;
  wrapper.appendChild(el);

  document.getElementById('timeline-toggle').addEventListener('click', () => {
    el.classList.toggle('expanded');
  }, { signal: _ac.signal });

  _timelineState.element = el;
}

function renderTimeline() {
  createTimelineElement();
  const container = document.getElementById('timeline-points');
  if (!container || _timelineState.studies.length === 0) return;

  container.innerHTML = '';

  const studies = _timelineState.studies;
  const minDate = new Date(studies[0].date).getTime();
  const maxDate = new Date(studies[studies.length - 1].date).getTime();
  const range = maxDate - minDate || 1;

  studies.forEach((s, i) => {
    const t = new Date(s.date).getTime();
    const pct = studies.length === 1 ? 50 : ((t - minDate) / range) * 90 + 5;

    const point = document.createElement('div');
    point.className = 'timeline-point';
    point.style.left = pct + '%';
    point.dataset.index = i;

    const dateStr = new Date(s.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: '2-digit' });
    const modalityBadge = s.modality || '';

    point.innerHTML = `
      <div class="timeline-dot${i === 0 ? ' first' : ''}${i === studies.length - 1 ? ' last' : ''}"></div>
      <div class="timeline-tooltip">
        <div class="timeline-tt-date">${dateStr}</div>
        <div class="timeline-tt-label">${s.label || s.modality || 'Серия'}</div>
        ${modalityBadge ? `<span class="timeline-tt-mod">${modalityBadge}</span>` : ''}
      </div>
    `;

    // Interval labels between points
    if (i > 0) {
      const prevDate = new Date(studies[i-1].date).getTime();
      const daysDiff = Math.round((t - prevDate) / 86400000);
      if (daysDiff > 0) {
        const prevPct = studies.length === 1 ? 50 : ((prevDate - minDate) / range) * 90 + 5;
        const midPct = (pct + prevPct) / 2;
        const interval = document.createElement('div');
        interval.className = 'timeline-interval';
        interval.style.left = midPct + '%';
        interval.textContent = daysDiff < 30 ? `${daysDiff}д` : daysDiff < 365 ? `${Math.round(daysDiff/30)}мес` : `${(daysDiff/365).toFixed(1)}г`;
        container.appendChild(interval);
      }
    }

    point.addEventListener('click', () => {
      emit('timeline:select', { index: i, study: s });
      // Highlight active
      container.querySelectorAll('.timeline-point').forEach(p => p.classList.remove('active'));
      point.classList.add('active');
    }, { signal: _ac.signal });

    container.appendChild(point);
  });

  // Show timeline if we have data
  _timelineState.element?.classList.add('has-data');
}

/** Hook: auto-populate from loaded DICOM data */
function hookTimelineFromDicom() {
  on('studies:analyzed', (detail) => {
    if (detail?.studies?.length > 1) {
      updateTimeline(detail.studies);
    }
  });
}


// ════════════════════════════════════════════════════════════════
// 12. DARK / WARM NIGHT MODE
// ════════════════════════════════════════════════════════════════

const NIGHT_LS_KEY = 'mikhmel_nightmode';
const _nightMode = {
  enabled: false,
  auto: true,
  startHour: 20,
  endHour: 7,
  timer: null
};

export function isNightMode() { return _nightMode.enabled; }

export function setNightMode(on) {
  _nightMode.enabled = on;
  document.documentElement.classList.toggle('night-warm', on);
  
  // Persist
  try { localStorage.setItem(NIGHT_LS_KEY, JSON.stringify({ auto: _nightMode.auto, startHour: _nightMode.startHour, endHour: _nightMode.endHour })); } catch { /* non-critical */ }
  
  emit('nightmode:changed', { enabled: on });
  log.debug(`[Premium] Night mode: ${on ? 'ON (warm amber)' : 'OFF (cool blue)'}`);
}

export function toggleNightMode() { setNightMode(!_nightMode.enabled); }

function checkNightSchedule() {
  if (!_nightMode.auto) return;
  const hour = new Date().getHours();
  const shouldBeNight = hour >= _nightMode.startHour || hour < _nightMode.endHour;
  if (shouldBeNight !== _nightMode.enabled) setNightMode(shouldBeNight);
}

function initNightMode() {
  // Load settings
  try {
    const saved = JSON.parse(localStorage.getItem(NIGHT_LS_KEY) || '{}');
    if (saved.auto !== undefined) _nightMode.auto = saved.auto;
    if (saved.startHour !== undefined) _nightMode.startHour = saved.startHour;
    if (saved.endHour !== undefined) _nightMode.endHour = saved.endHour;
  } catch (e) { log.debug("[premiumFeatures]", e.message); }

  checkNightSchedule();
  // [FIX] Очистка предыдущего интервала (защита от двойной инициализации)
  if (_nightMode.timer) clearInterval(_nightMode.timer);
  // Check every 5 minutes
  _nightMode.timer = setInterval(checkNightSchedule, 5 * 60 * 1000);
}

/** [FIX] Очистка ресурсов Night Mode */
export function destroyNightMode() {
  _ac.abort(); _ac = new AbortController();
  if (_nightMode.timer) { clearInterval(_nightMode.timer); _nightMode.timer = null; }
}


// ════════════════════════════════════════════════════════════════
// 13. BREADCRUMB NAVIGATION
// ════════════════════════════════════════════════════════════════

const BREADCRUMB_ID = 'breadcrumb-nav';
const _breadcrumbState = {
  patient: null,
  study: null,
  series: null,
  slice: null,
  totalSlices: null
};

export function updateBreadcrumb(data) {
  // DISABLED — breadcrumb removed per user request
  return;
}

function createBreadcrumbElement() {
  if (document.getElementById(BREADCRUMB_ID)) return document.getElementById(BREADCRUMB_ID);

  const wrapper = document.getElementById('viewport-wrapper');
  if (!wrapper) return null;

  const el = document.createElement('div');
  el.id = BREADCRUMB_ID;
  el.className = 'breadcrumb-nav';
  
  wrapper.appendChild(el);
  return el;
}

function renderBreadcrumb() {
  const el = createBreadcrumbElement();
  if (!el) return;

  const s = _breadcrumbState;
  const crumbs = [];

  if (s.patient) {
    crumbs.push({ label: s.patient, icon: '', action: 'breadcrumb:patient' });
  }
  if (s.study) {
    crumbs.push({ label: s.study, icon: '', action: 'breadcrumb:study' });
  }
  if (s.series) {
    crumbs.push({ label: s.series, icon: '', action: 'breadcrumb:series' });
  }
  if (s.slice != null && s.totalSlices != null) {
    crumbs.push({ label: `Срез ${s.slice}/${s.totalSlices}`, icon: '', action: null });
  }

  if (crumbs.length === 0) {
    el.style.display = 'none';
    return;
  }

  el.style.display = 'flex';
  el.innerHTML = crumbs.map((c, i) => {
    const sep = i < crumbs.length - 1 ? '<span class="bc-sep">›</span>' : '';
    const clickable = c.action ? `data-action="${c.action}" role="button" tabindex="0"` : '';
    return `<span class="bc-item${c.action ? ' bc-clickable' : ''}" ${clickable}>${c.icon ? c.icon + ' ' : ''}${c.label}</span>${sep}`;
  }).join('');

  // Attach click handlers
  el.querySelectorAll('[data-action]').forEach(item => {
    item.addEventListener('click', () => emit(item.dataset.action, _breadcrumbState), { signal: _ac.signal });
  });
}

/** Hook: auto-update breadcrumb from viewport info events */
function hookBreadcrumbEvents() {
  on(EVENTS.VIEWPORT_INFO_UPDATE, (detail) => {
    if (!detail) return;
    updateBreadcrumb({
      patient: detail.patientName || detail.patient || null,
      study: detail.studyDescription || detail.study || null,
      series: detail.seriesDescription || detail.series || null,
      slice: detail.sliceIndex ?? detail.currentSlice ?? null,
      totalSlices: detail.totalSlices ?? detail.numSlices ?? null
    });
  });

  // Also listen to overlay updates as fallback
  const overlayPatient = document.getElementById('overlay-patient');
  const overlaySlice = document.getElementById('overlay-slice');
  const overlayStudy = document.getElementById('overlay-study');

  if (overlayPatient || overlaySlice) {
    const mo = new MutationObserver(() => {
      const patient = overlayPatient?.textContent?.trim() || null;
      const study = overlayStudy?.textContent?.trim() || null;
      const sliceText = overlaySlice?.textContent || '';
      const sliceMatch = sliceText.match(/(\d+)\s*\/\s*(\d+)/);
      
      if (patient || study || sliceMatch) {
        updateBreadcrumb({
          patient: patient || _breadcrumbState.patient,
          study: study || _breadcrumbState.study,
          slice: sliceMatch ? parseInt(sliceMatch[1]) : _breadcrumbState.slice,
          totalSlices: sliceMatch ? parseInt(sliceMatch[2]) : _breadcrumbState.totalSlices
        });
      }
    });
    if (overlayPatient) mo.observe(overlayPatient, { childList: true, characterData: true, subtree: true });
    if (overlaySlice) mo.observe(overlaySlice, { childList: true, characterData: true, subtree: true });
    if (overlayStudy) mo.observe(overlayStudy, { childList: true, characterData: true, subtree: true });
  }
}


// ════════════════════════════════════════════════════════════════
// CSS INJECTION
// ════════════════════════════════════════════════════════════════

function injectStyles() {
  if (document.getElementById('premium-features-css')) return;

  const style = document.createElement('style');
  style.id = 'premium-features-css';
  style.textContent = `

/* ═══════════════════════════════════════════
   7. CINEMATIC WELCOME SCREEN
   ═══════════════════════════════════════════ */

#cinematic-welcome {
  position: absolute; inset: 0; z-index: 10;
  background: #060810;
  background-image:
    radial-gradient(ellipse at 50% 35%, rgba(79,140,255,0.06) 0%, transparent 55%),
    radial-gradient(ellipse at 20% 80%, rgba(34,211,238,0.03) 0%, transparent 40%),
    radial-gradient(ellipse at 80% 70%, rgba(167,139,250,0.03) 0%, transparent 40%);
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
  animation: welcomeFadeIn 0.6s ease-out;
}
/* [10.0.2] Короткий экран (телефон набок, окно в половину высоты): заставка
   выше доступного места, и её низ — кнопка «Открыть файлы» — оказывался
   срезан ancestor'ом с overflow:hidden. Разрешаем прокрутку и ужимаем
   картинку сканера: лучше меньше, чем недоступно. */
@media (max-height: 620px) {
  #cinematic-welcome { align-items: flex-start; overflow-y: auto; overflow-x: hidden; }
  .welcome-content { padding-top: 14px; padding-bottom: 14px; }
  .welcome-scan-viz { width: 120px; height: 120px; margin-bottom: 8px; }
}
#cinematic-welcome.dismissing {
  animation: welcomeFadeOut 0.5s ease-in forwards;
}
@keyframes welcomeFadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes welcomeFadeOut { to { opacity: 0; transform: scale(1.03); } }

#welcome-canvas {
  position: absolute; inset: 0; width: 100%; height: 100%;
  pointer-events: none; opacity: 0.4;
}

.welcome-content {
  position: relative; z-index: 2;
  display: flex; flex-direction: column; align-items: center;
  gap: 0; width: 100%; max-width: 600px; padding: 0 20px;
}

/* CT Scanner visualization */
.welcome-scan-viz {
  width: 200px; height: 200px; margin-bottom: 16px;
  animation: welcomeElIn 1s ease-out 0.2s both;
}
.scan-svg { width: 100%; height: 100%; }
.scan-rotate {
  transform-origin: 140px 140px;
  animation: scanSpin 8s linear infinite;
}
@keyframes scanSpin { to { transform: rotate(360deg); } }
.scan-dot { animation: dotPulse 2s ease-in-out infinite; }
@keyframes dotPulse { 0%,100% { r: 3; opacity: 0.7; } 50% { r: 5; opacity: 1; } }
.scan-line {
  animation: scanSweep 3s ease-in-out infinite;
  transform-origin: center;
}
@keyframes scanSweep {
  0% { transform: translateY(-60px); opacity: 0; }
  10% { opacity: 1; }
  50% { transform: translateY(0px); opacity: 0.8; }
  90% { opacity: 1; }
  100% { transform: translateY(60px); opacity: 0; }
}

/* Hero text */
.welcome-hero-text {
  text-align: center; margin-bottom: 28px;
  animation: welcomeElIn 1s ease-out 0.4s both;
}
.welcome-label {
  font-size: 10px; letter-spacing: 0.35em; color: rgba(79,140,255,0.5);
  text-transform: uppercase; margin-bottom: 8px; font-weight: 500;
}
.welcome-title {
  font-size: clamp(36px, 8vw, 56px); font-weight: 800; letter-spacing: -0.04em;
  color: #e8eaf0; line-height: 1;
  text-shadow: 0 0 60px rgba(79,140,255,0.15);
}
.welcome-4d {
  background: linear-gradient(135deg, #4f8cff, #22d3ee);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  background-clip: text; font-weight: 900;
}
.welcome-version {
  font-size: 11px; color: rgba(255,255,255,0.25); margin-top: 8px;
  letter-spacing: 0.05em;
}
/* Сам номер — ярче остальной строки: за ним и приходят взглядом. */
.welcome-ver-num { color: rgba(79,140,255,0.85); font-weight: 700; }

/* Drop zone */
.welcome-drop-zone {
  position: relative; width: 100%; max-width: 340px;
  padding: 28px 40px; cursor: pointer;
  border-radius: 14px;
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  transition: all 0.3s ease;
  animation: welcomeElIn 1s ease-out 0.6s both;
}
.welcome-drop-zone:hover { transform: scale(1.02); }
.welcome-drop-zone:hover .welcome-drop-ring { border-color: rgba(79,140,255,0.5); box-shadow: 0 0 40px rgba(79,140,255,0.08); }
.welcome-drop-zone:hover .welcome-drop-icon svg { stroke: rgba(79,140,255,1); transform: translateY(-2px); }

.welcome-drop-ring {
  position: absolute; inset: 0;
  border: 1px solid rgba(79,140,255,0.15);
  border-radius: 14px;
  transition: all 0.4s ease;
}
.welcome-drop-icon svg { transition: all 0.3s ease; }
.welcome-drop-text {
  font-size: 15px; font-weight: 500; color: rgba(79,140,255,0.8);
}
.welcome-drop-hint {
  font-size: 11px; color: rgba(255,255,255,0.2);
}

/* Stats bar */
.welcome-stats {
  display: flex; align-items: center; gap: 16px;
  margin-top: 32px; padding: 12px 24px;
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.04);
  border-radius: 10px;
  animation: welcomeElIn 1s ease-out 0.8s both;
}
.ws-item { text-align: center; }
.ws-val {
  display: block; font-size: 13px; font-weight: 700;
  color: rgba(79,140,255,0.9); letter-spacing: -0.02em;
}
.ws-label {
  display: block; font-size: 8px; color: rgba(255,255,255,0.2);
  text-transform: uppercase; letter-spacing: 0.08em; margin-top: 2px;
}
.ws-sep { width: 1px; height: 24px; background: rgba(255,255,255,0.05); }

/* Tech line */
.welcome-tech {
  margin-top: 16px; font-size: 9px; color: rgba(255,255,255,0.12);
  letter-spacing: 0.03em; text-align: center;
  animation: welcomeElIn 1s ease-out 1s both;
}

/* Staggered entrance */
@keyframes welcomeElIn {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Drag active state */
#cinematic-welcome.drag-active .welcome-drop-zone {
  background: rgba(79,140,255,0.06);
}
#cinematic-welcome.drag-active .welcome-drop-ring {
  border-color: rgba(79,140,255,0.6);
  box-shadow: 0 0 50px rgba(79,140,255,0.12), inset 0 0 30px rgba(79,140,255,0.03);
}
#cinematic-welcome.drag-active .welcome-drop-icon svg {
  stroke: #4f8cff; animation: dropBounce 0.6s ease infinite;
}
@keyframes dropBounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }

@media (max-width: 600px) {
  .welcome-scan-viz { width: 140px; height: 140px; }
  .welcome-stats { gap: 10px; padding: 10px 16px; flex-wrap: wrap; justify-content: center; }
  .welcome-drop-zone { max-width: 280px; padding: 24px 28px; }
}

/* ═══════════════════════════════════════════
   8. PIP WINDOW
   ═══════════════════════════════════════════ */

.pip-window {
  position: absolute; bottom: 16px; right: 16px;
  width: 200px;
  background: rgba(10,12,20,0.95);
  border: 1px solid rgba(79,140,255,0.3);
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.6), 0 0 1px rgba(79,140,255,0.3);
  z-index: 50;
  overflow: hidden;
  transition: width 0.3s var(--ease-out-expo), box-shadow 0.2s;
  animation: pipSlideIn 0.35s var(--ease-out-expo);
}
@keyframes pipSlideIn {
  from { opacity: 0; transform: translateY(20px) scale(0.9); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.pip-window:hover { box-shadow: 0 8px 32px rgba(0,0,0,0.6), 0 0 8px rgba(79,140,255,0.2); }

.pip-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 4px 8px; background: rgba(79,140,255,0.08);
  cursor: grab; user-select: none;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.pip-header:active { cursor: grabbing; }
.pip-label { font-size: 10px; color: var(--text-secondary); font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pip-controls { display: flex; gap: 2px; }
.pip-btn {
  width: 20px; height: 20px; border: none; background: transparent;
  color: var(--text-dim); font-size: 10px; border-radius: 3px;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.pip-btn:hover { background: rgba(255,255,255,0.1); color: var(--text-primary); }

.pip-body { line-height: 0; }
.pip-canvas { width: 100%; height: auto; display: block; image-rendering: auto; }

.pip-footer {
  padding: 3px 8px; background: rgba(0,0,0,0.3);
}
.pip-opacity {
  width: 100%; height: 3px; -webkit-appearance: none; appearance: none;
  background: rgba(255,255,255,0.1); border-radius: 2px; outline: none;
}
.pip-opacity::-webkit-slider-thumb {
  -webkit-appearance: none; width: 10px; height: 10px;
  border-radius: 50%; background: var(--accent); cursor: pointer;
}

/* ═══════════════════════════════════════════
   10. GESTURE SHORTCUTS
   ═══════════════════════════════════════════ */

.gesture-shortcut-canvas {
  position: absolute; inset: 0; z-index: 60;
  pointer-events: none; opacity: 0;
  transition: opacity 0.3s;
}

.gesture-result-indicator {
  position: absolute; top: 50%; left: 50%;
  transform: translate(-50%, -50%) scale(0.8);
  background: rgba(10,14,28,0.9);
  border: 1px solid rgba(79,140,255,0.4);
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 16px; font-weight: 600; color: var(--accent);
  white-space: nowrap; z-index: 70;
  opacity: 0; transition: all 0.3s var(--ease-out-expo);
  box-shadow: 0 4px 24px rgba(0,0,0,0.4), 0 0 16px rgba(79,140,255,0.15);
  pointer-events: none;
}
.gesture-result-indicator.visible {
  opacity: 1; transform: translate(-50%, -50%) scale(1);
}

/* ═══════════════════════════════════════════
   11. TIMELINE SCRUBBER
   ═══════════════════════════════════════════ */

.timeline-scrubber {
  position: absolute; bottom: 0; left: 0; right: 0;
  height: 4px; z-index: 20;
  transition: height 0.35s var(--ease-out-expo), background 0.3s;
  overflow: hidden;
  display: none; /* hidden until has-data */
}
.timeline-scrubber.has-data { display: block; }
.timeline-scrubber.expanded {
  height: 52px;
  background: rgba(6,8,16,0.92);
  backdrop-filter: blur(8px);
  border-top: 1px solid rgba(79,140,255,0.12);
}

.timeline-track {
  position: relative; width: 100%; height: 100%;
  padding: 0 20px;
}

.timeline-line {
  position: absolute; top: 50%; left: 20px; right: 20px;
  height: 2px; background: rgba(79,140,255,0.15);
  transform: translateY(-50%);
  border-radius: 1px;
}

.timeline-toggle {
  position: absolute; right: 4px; top: -22px;
  width: 22px; height: 22px;
  background: rgba(10,14,28,0.8); border: 1px solid rgba(79,140,255,0.2);
  border-radius: 4px; color: var(--text-dim);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.2s;
}
.timeline-toggle:hover { color: var(--accent); border-color: var(--accent); }
.timeline-scrubber.expanded .timeline-toggle { top: 4px; right: 4px; }

.timeline-points { position: relative; width: 100%; height: 100%; }

.timeline-point {
  position: absolute; top: 50%; transform: translate(-50%, -50%);
  cursor: pointer; z-index: 2;
}
.timeline-dot {
  width: 10px; height: 10px; border-radius: 50%;
  background: var(--accent);
  border: 2px solid rgba(10,14,28,0.8);
  transition: all 0.2s;
  box-shadow: 0 0 6px rgba(79,140,255,0.3);
}
.timeline-dot.first { background: var(--success); box-shadow: 0 0 6px rgba(52,211,153,0.3); }
.timeline-dot.last { background: var(--cyan); box-shadow: 0 0 6px rgba(34,211,238,0.3); }
.timeline-point:hover .timeline-dot { transform: scale(1.4); }
.timeline-point.active .timeline-dot { transform: scale(1.5); box-shadow: 0 0 12px rgba(79,140,255,0.6); }

.timeline-tooltip {
  position: absolute; bottom: calc(100% + 6px); left: 50%;
  transform: translateX(-50%);
  background: rgba(10,14,28,0.95);
  border: 1px solid rgba(79,140,255,0.2);
  border-radius: 6px; padding: 4px 8px;
  white-space: nowrap; pointer-events: none;
  opacity: 0; transition: opacity 0.2s;
  box-shadow: 0 4px 12px rgba(0,0,0,0.5);
}
.timeline-point:hover .timeline-tooltip { opacity: 1; }
.timeline-tt-date { font-size: 10px; color: var(--accent); font-weight: 500; }
.timeline-tt-label { font-size: 9px; color: var(--text-secondary); }
.timeline-tt-mod { font-size: 8px; background: rgba(79,140,255,0.15); color: var(--accent); padding: 1px 4px; border-radius: 3px; margin-top: 2px; display: inline-block; }

.timeline-interval {
  position: absolute; top: 50%; transform: translate(-50%, 10px);
  font-size: 8px; color: rgba(255,255,255,0.25);
  white-space: nowrap; pointer-events: none;
}

/* ═══════════════════════════════════════════
   12. NIGHT WARM MODE
   ═══════════════════════════════════════════ */

html.night-warm {
  --accent: #e8a84c;
  --accent-hover: #d4943a;
  --accent-glow: rgba(232,168,76,0.2);
  --accent-subtle: rgba(232,168,76,0.1);
  --border-accent: rgba(232,168,76,0.3);
  --bg-primary: #12100e;
  --bg-secondary: #1a1614;
  --bg-panel: #15120f;
  --bg-panel-alt: #18150f;
  --bg-hover: rgba(232,168,76,0.06);
  --bg-active: rgba(232,168,76,0.1);
  --text-primary: #e8ddd0;
  --text-secondary: #b8a898;
  --text-dim: #8a7a6b;
  --cyan: #d4943a;
  --purple: #c4956a;
  filter: none;
}
html.night-warm body {
  background: var(--bg-primary);
}
/* Не трогаем viewport canvas — врачу нужны достоверные цвета */
html.night-warm #viewport-container canvas {
  filter: none !important;
}
/* Анимация перехода */
html.night-warm * {
  transition: background-color 1s ease, color 0.8s ease, border-color 0.8s ease;
}

/* Индикатор ночного режима */
html.night-warm .topbar::before {
  content: '🌙';
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  font-size: 10px; opacity: 0.4;
  pointer-events: none;
}

/* ═══════════════════════════════════════════
   13. BREADCRUMB NAVIGATION
   ═══════════════════════════════════════════ */

.breadcrumb-nav {
  display: none !important; /* DISABLED — breadcrumb removed per user request */
}
.bc-item {
  font-size: 11px; color: var(--text-dim);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 160px;
  transition: color 0.15s;
}
.bc-item.bc-clickable {
  cursor: pointer; color: var(--text-secondary);
}
.bc-item.bc-clickable:hover {
  color: var(--accent);
}
.bc-sep {
  font-size: 10px; color: rgba(255,255,255,0.15);
  flex-shrink: 0; line-height: 1;
}

/* Mobile: скрываем breadcrumb */
@media (max-width: 768px) {
  .breadcrumb-nav { display: none !important; }
  .timeline-scrubber { display: none !important; }
  .pip-window { width: 140px !important; bottom: 60px !important; }
}

/* ═══════════════════════════════════════════
   9. AI VOICE STATUS — indicator badge
   ═══════════════════════════════════════════ */

.voice-status-badge {
  position: fixed; bottom: 8px; left: 8px;
  padding: 3px 8px; border-radius: 12px;
  font-size: 9px; font-weight: 500;
  background: rgba(79,140,255,0.15);
  color: var(--accent); border: 1px solid rgba(79,140,255,0.2);
  z-index: 30; pointer-events: none;
  animation: voiceBadgePulse 2s ease-in-out infinite;
  display: none;
}
.voice-status-badge.active { display: flex; align-items: center; gap: 4px; }
@keyframes voiceBadgePulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

  `;
  document.head.appendChild(style);
}


// ════════════════════════════════════════════════════════════════
// INIT — master initializer
// ════════════════════════════════════════════════════════════════

let _initialized = false;

export function initPremiumFeatures() {
  if (_initialized) return;
  _initialized = true;

  injectStyles();

  // 7. Welcome Screen
  createWelcomeScreen();
  hookWelcomeDismiss();

  // 9. Voice Status (hooked but not enabled by default — user toggles)
  hookVoiceEvents();

  // 11. Timeline
  hookTimelineFromDicom();

  // 12. Night Mode
  initNightMode();

  // 13. Breadcrumb — DISABLED (removed per user request)
  // hookBreadcrumbEvents();
  // Also remove any existing breadcrumb element
  const _existingBC = document.getElementById(BREADCRUMB_ID);
  if (_existingBC) _existingBC.remove();

  // 10. Gesture (enabled explicitly by user or from Enterprise panel)
  // enableGestureShortcuts(); // uncomment for auto-enable

  log.debug('[Premium] All premium features initialized');
}

// Export for Enterprise Panel integration (only items not already exported inline)
export {
  dismissWelcome,
  speak as voiceSpeak
};
